/**
 * Telemetry — typed wrapper over Firebase Analytics for the
 * Suggestion / Vote / ExecutedAction surface (PR 7).
 *
 * The six event names + their parameter shapes come from the
 * redesign brief sec 4. Keep the union in sync if events get
 * added/removed there.
 *
 * Implementation notes:
 *   - Firebase Analytics is initialized in src/firebase.ts but was
 *     never used. This module is the first caller. `getAnalytics()`
 *     no-ops in environments without `window` (SSR / tests), so we
 *     guard the call and fall through silently — analytics calls
 *     should never throw in user code.
 *   - In CRA dev (NODE_ENV !== 'production') we ALSO console.log
 *     each event so engineers can see the wiring without opening
 *     the Firebase console.
 */
import {getAnalytics, logEvent, isSupported} from 'firebase/analytics';
import {app} from '../firebase';

export type TelemetryEvent =
  | {name: 'suggestion_viewed'; params: {id: string; verb: string; persona: 'parent' | 'admin'}}
  | {name: 'suggestion_applied'; params: {id: string; verb: string; source: 'row_button' | 'sheet_primary'}}
  | {name: 'suggestion_snoozed'; params: {id: string; verb: string; days: number}}
  | {name: 'suggestion_dismissed'; params: {id: string; verb: string}}
  | {name: 'suggestion_flagged_wrong'; params: {id: string; verb: string; reason?: string | null}}
  | {name: 'activity_tab_viewed'; params: {tab: 'youtube' | 'decisions' | 'pending'}}
  // Fires when an Activity row is clicked and the EvidenceSheet docks
  // on the right. id is the row's stable id (executed-event id or
  // composite vote id). persona lets us split parent vs admin reads.
  | {name: 'activity_row_opened'; params: {tab: 'youtube' | 'decisions'; id: string; persona: 'parent' | 'admin'}}
  // Fires when the parent clicks "Undo this vote" from the
  // EvidenceSheet docked on /activity/decisions.
  | {name: 'decision_undo_clicked'; params: {voteId: string; verb: string}}
  // Fires when the slim Pending callout's "Open Review queue" button
  // is clicked. count is how many pending suggestions the callout
  // advertised at click time.
  | {name: 'activity_pending_open_review_clicked'; params: {count: number}};

let analyticsPromise: Promise<ReturnType<typeof getAnalytics> | null> | null = null;

function getAnalyticsInstance() {
  if (!analyticsPromise) {
    analyticsPromise = isSupported()
      .then(supported => (supported ? getAnalytics(app) : null))
      .catch(() => null);
  }
  return analyticsPromise;
}

/**
 * Fire one telemetry event. Always returns immediately — the
 * Firebase call resolves asynchronously and any failure is swallowed.
 * Telemetry must NEVER block user interaction or surface errors.
 */
export function track<E extends TelemetryEvent>(event: E['name'], params: Extract<TelemetryEvent, {name: E['name']}>['params']): void {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[telemetry]', event, params);
  }
  getAnalyticsInstance()
    .then(analytics => {
      if (!analytics) return;
      logEvent(analytics, event, params as Record<string, unknown>);
    })
    .catch(() => {
      // Swallow — analytics must never throw to user code.
    });
}
